import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, LOGO_URL, formatDateTime } from "@/lib/clinic";
import { useNotificaChat } from "@/hooks/useNotificaChat";
import { MessageCircle, Send, X, Users, Volume2, VolumeX } from "lucide-react";

const NICK_KEY = "mab_chat_nick";

export const ChatWidget = ({ lastEvent }) => {
  const [open, setOpen] = useState(false);
  const [nick, setNick] = useState(() => localStorage.getItem(NICK_KEY) || "");
  const [tempNick, setTempNick] = useState("");
  const [messaggi, setMessaggi] = useState([]);
  const [testo, setTesto] = useState("");
  const [nonLetti, setNonLetti] = useState(0);
  const listaRef = useRef(null);
  const openRef = useRef(false);
  const { play: suona, attivo: audioAttivo, setAttivo: setAudioAttivo } = useNotificaChat();

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    api.get("/chat").then(({ data }) => setMessaggi(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (lastEvent?.type === "CHAT_MESSAGE" && lastEvent.message) {
      setMessaggi((m) => (m.some((x) => x.id === lastEvent.message.id) ? m : [...m, lastEvent.message]));
      if (!openRef.current) setNonLetti((n) => n + 1);
      if (lastEvent.message.autore !== nick) suona();
    }
    if (lastEvent?.type === "CHAT_CLEARED") setMessaggi([]);
  }, [lastEvent]);

  useEffect(() => {
    if (open) setNonLetti(0);
  }, [open]);

  // All'apertura la chat si posiziona sempre sull'ultimo messaggio ricevuto.
  useEffect(() => {
    if (!open || !nick) return;
    const scorri = () => {
      const lista = listaRef.current;
      if (lista) lista.scrollTop = lista.scrollHeight;
    };
    scorri();
    const t1 = setTimeout(scorri, 60);
    const t2 = setTimeout(scorri, 260);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [open, nick, messaggi]);

  const invia = async (e) => {
    e.preventDefault();
    if (!testo.trim()) return;
    const corpo = testo;
    setTesto("");
    try {
      await api.post("/chat", { autore: nick || "Operatore", testo: corpo });
    } catch {
      setTesto(corpo);
    }
  };

  const salvaNick = (e) => {
    e.preventDefault();
    const n = tempNick.trim().slice(0, 40);
    if (!n) return;
    localStorage.setItem(NICK_KEY, n);
    setNick(n);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-toggle-btn"
          title={nonLetti > 0 ? `${nonLetti} nuovi messaggi in chat` : "Chat operatori"}
          className={`fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-slate-900 text-white shadow-xl shadow-slate-900/25 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center ${
            nonLetti > 0 ? "chat-blink" : ""
          }`}
        >
          <MessageCircle className="w-9 h-9 absolute text-slate-900/0" />
          <span className="relative flex items-center justify-center">
            <MessageCircle className="w-8 h-8" strokeWidth={1.4} />
            <img
              src={LOGO_URL}
              alt="MAB"
              className="absolute h-2.5 w-auto invert -translate-y-[2px]"
              draggable={false}
            />
          </span>
          {nonLetti > 0 && (
            <span
              data-testid="chat-unread-badge"
              className="absolute -top-1 -right-1 min-w-[22px] h-[22px] rounded-full bg-rose-500 text-[11px] font-bold flex items-center justify-center px-1"
            >
              {nonLetti}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[370px] h-[62vh] sm:h-[470px] rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-slate-900 text-white">
            <img src={LOGO_URL} alt="MAB" className="h-4 w-auto invert" draggable={false} />
            <div className="flex-1">
              <div className="font-display text-sm font-bold uppercase tracking-[0.18em]">Chat operatori</div>
              <button
                onClick={() => {
                  setTempNick(nick);
                  setNick("");
                }}
                data-testid="chat-change-nick-btn"
                title="Cambia operatore"
                className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-white flex items-center gap-1"
              >
                <Users className="w-3 h-3" /> {nick || "anonimo"} · cambia
              </button>
            </div>
            <button
              onClick={() => setAudioAttivo(!audioAttivo)}
              data-testid="chat-sound-toggle-btn"
              data-audio={audioAttivo ? "on" : "off"}
              title={audioAttivo ? "Avviso sonoro attivo in questa postazione" : "Avviso sonoro silenziato in questa postazione"}
              className={`transition-colors ${audioAttivo ? "text-emerald-400 hover:text-emerald-300" : "text-slate-500 hover:text-white"}`}
            >
              {audioAttivo ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="text-slate-300 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {!nick ? (
            <form onSubmit={salvaNick} className="flex-1 flex flex-col justify-center gap-3 p-6" data-testid="chat-nick-form">
              <div className="text-sm text-slate-600">Chi è in postazione adesso? Il nome viene mostrato ai colleghi.</div>
              <Input
                value={tempNick}
                onChange={(e) => setTempNick(e.target.value)}
                placeholder="Es. Accettazione 1"
                data-testid="chat-nick-input"
              />
              <Button type="submit" className="rounded-full" data-testid="chat-nick-save-btn">
                Entra in chat
              </Button>
            </form>
          ) : (
            <>
              <div ref={listaRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
                {messaggi.map((m) => {
                  const mio = m.autore === nick;
                  return (
                    <div key={m.id} className={`flex ${mio ? "justify-end" : "justify-start"}`} data-testid="chat-message">
                      <div
                        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                          mio ? "bg-slate-900 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"
                        }`}
                      >
                        {!mio && <div className="text-[10px] font-semibold uppercase tracking-widest opacity-60">{m.autore}</div>}
                        <div className="whitespace-pre-wrap break-words">{m.testo}</div>
                        <div className={`text-[10px] mt-1 ${mio ? "text-slate-400" : "text-slate-400"}`}>
                          {formatDateTime(m.at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {messaggi.length === 0 && (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400 text-center px-6">
                    Nessun messaggio. Scrivi per coordinarti con le sale.
                  </div>
                )}
              </div>

              <form onSubmit={invia} className="flex items-center gap-2 border-t border-slate-200 p-3">
                <Input
                  value={testo}
                  onChange={(e) => setTesto(e.target.value)}
                  placeholder="Scrivi un messaggio..."
                  data-testid="chat-input"
                />
                <Button type="submit" size="icon" className="rounded-full shrink-0" data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};
